export default class AudioRecorder {
    constructor() {
        this.mediaRecorder = null;
        this.audioChunks = [];
        this.stream = null;
        this.isRecording = false;
    }

    // Inicializar o gravador e solicitar permissão de microfone
    async init() {
        try {
            this.stream = await navigator.mediaDevices.getUserMedia({
                audio: {
                    echoCancellation: true,
                    noiseSuppression: true,
                    autoGainControl: true
                }
            });

            this.mediaRecorder = new MediaRecorder(this.stream, {
                mimeType: this.getSupportedMimeType()
            });

            this.setupEventHandlers();
            console.log('Gravador inicializado com sucesso');
            return true;
        } catch (error) {
            console.error('Erro ao inicializar gravador:', error);
            return false;
        }
    }

    getSupportedMimeType() {
        const types = [
            'audio/ogg;codecs=opus',
            'audio/opus',
            'audio/ogg',
            'audio/mpeg',
            'audio/mp4',
            'audio/aac',
            'audio/amr',
        ];

        for (const type of types) {
            if (MediaRecorder.isTypeSupported(type)) {
                return type;
            }
        }
        
        if (MediaRecorder.isTypeSupported('audio/opus')) {
            return 'audio/opus';
        }
        
        return 'audio/ogg';
    }

    // Configurar event handlers do MediaRecorder
    setupEventHandlers() {
        this.mediaRecorder.ondataavailable = (event) => {
            if (event.data.size > 0) {
                this.audioChunks.push(event.data);
            }
        };

        this.mediaRecorder.onstart = () => {
            console.log('Gravação iniciada');
            this.isRecording = true;
            this.onRecordingStart && this.onRecordingStart();
        };

        this.mediaRecorder.onstop = () => {
            console.log('Gravação parada');
            this.isRecording = false;
            
            // Processar gravação antes de chamar o callback
            const audioData = this.processRecording();
            
            // Chamar callback de parada
            this.onRecordingStop && this.onRecordingStop();
            
            // Se processamento falhou, o erro já foi tratado em processRecording
            if (!audioData && this.onRecordingError) {
                // Erro já foi chamado em processRecording, não precisa chamar novamente
            }
        };

        this.mediaRecorder.onerror = (event) => {
            console.error('Erro na gravação:', event.error);
            this.onRecordingError && this.onRecordingError(event.error);
        };
    }

    // Iniciar gravação
    startRecording() {
        if (!this.mediaRecorder) {
            console.error('MediaRecorder não foi inicializado');
            return false;
        }

        if (this.isRecording) {
            console.warn('Gravação já está em andamento');
            return false;
        }

        this.audioChunks = []; // Limpar chunks anteriores

        try {
            // Solicitar dados a cada 100ms para gravação contínua
            this.mediaRecorder.start(100);
            return true;
        } catch (error) {
            console.error('Erro ao iniciar gravação:', error);
            return false;
        }
    }

    // Parar gravação
    stopRecording() {
        if (!this.mediaRecorder) {
            console.warn('MediaRecorder não foi inicializado');
            return false;
        }

        // Verificar o estado do MediaRecorder
        if (this.mediaRecorder.state === 'inactive') {
            console.warn('Nenhuma gravação em andamento');
            this.isRecording = false;
            return false;
        }

        try {
            // Se estiver pausado, parar diretamente
            if (this.mediaRecorder.state === 'paused') {
                this.mediaRecorder.stop();
            } else if (this.mediaRecorder.state === 'recording') {
                this.mediaRecorder.stop();
            }
            return true;
        } catch (error) {
            console.error('Erro ao parar gravação:', error);
            this.isRecording = false;
            this.onRecordingError && this.onRecordingError(error);
            return false;
        }
    }

    // Processar gravação após parar
    processRecording() {
        if (this.audioChunks.length === 0) {
            console.warn('Nenhum chunk de áudio encontrado');
            this.isRecording = false;
            if (this.onRecordingError) {
                this.onRecordingError(new Error('Nenhum áudio foi gravado. Tente novamente.'));
            }
            return null;
        }

        try {
            const audioBlob = new Blob(this.audioChunks, {
                type: this.mediaRecorder.mimeType || 'audio/webm'
            });

            // Verificar se o blob tem tamanho válido
            if (audioBlob.size === 0) {
                console.warn('Blob de áudio está vazio');
                this.isRecording = false;
                if (this.onRecordingError) {
                    this.onRecordingError(new Error('Gravação está vazia. Tente novamente.'));
                }
                return null;
            }

            const audioUrl = URL.createObjectURL(audioBlob);

            const audioData = {
                blob: audioBlob,
                url: audioUrl,
                duration: this.getRecordingDuration(),
                mimeType: this.mediaRecorder.mimeType || 'audio/webm',
                size: audioBlob.size
            };

            this.onRecordingComplete && this.onRecordingComplete(audioData);
            return audioData;
        } catch (error) {
            console.error('Erro ao processar gravação:', error);
            this.isRecording = false;
            this.onRecordingError && this.onRecordingError(error);
            return null;
        }
    }

    // Obter duração estimada da gravação
    getRecordingDuration() {
        // Estimativa baseada no número de chunks (cada chunk é ~100ms)
        return this.audioChunks.length * 0.1;
    }

    // Pausar gravação (se suportado)
    pauseRecording() {
        if (this.mediaRecorder && this.isRecording && this.mediaRecorder.state === 'recording') {
            try {
                this.mediaRecorder.pause();
                this.onRecordingPause && this.onRecordingPause();
                return true;
            } catch (error) {
                console.error('Erro ao pausar gravação:', error);
                return false;
            }
        }
        return false;
    }

    // Retomar gravação
    resumeRecording() {
        if (this.mediaRecorder && this.mediaRecorder.state === 'paused') {
            try {
                this.mediaRecorder.resume();
                this.onRecordingResume && this.onRecordingResume();
                return true;
            } catch (error) {
                console.error('Erro ao retomar gravação:', error);
                return false;
            }
        }
        return false;
    }

    // Verificar status da gravação
    getRecordingState() {
        return {
            isRecording: this.isRecording,
            state: this.mediaRecorder ? this.mediaRecorder.state : 'inactive',
            chunksCount: this.audioChunks.length,
            estimatedDuration: this.getRecordingDuration()
        };
    }

    // Limpar recursos
    cleanup() {
        if (this.stream) {
            this.stream.getTracks().forEach(track => track.stop());
            this.stream = null;
        }

        this.audioChunks = [];
        this.mediaRecorder = null;
        this.isRecording = false;
    }

    // Callbacks que podem ser definidos externamente
    onRecordingStart = null;
    onRecordingStop = null;
    onRecordingComplete = null;
    onRecordingError = null;
    onRecordingPause = null;
    onRecordingResume = null;
}
